/**
 * UK housebuilder SEO landing page data.
 *
 * Each entry powers one /snag/<slug> page. To add a new builder later,
 * just append an object below — no other file changes required. The
 * BuilderSnagging.jsx template renders every field defensively, so a
 * minimum entry only needs name + slug + tagline + intro.
 *
 * Pricing / content is INFORMATIONAL only. Common defects are sourced
 * from publicly-available HBF Customer Satisfaction Survey data and
 * the Trustpilot/Reddit aggregate consumer experience — never from
 * private complaints. Keep it factual and defensible if a builder ever
 * objects.
 *
 * Legal: each page renders a small "not affiliated with X" disclaimer
 * in the footer — never claim association or use the builder's logo.
 */

export const HOUSEBUILDERS = [
    {
        slug: 'persimmon-homes',
        name: 'Persimmon Homes',
        legalName: 'Persimmon Homes Ltd',
        tagline: 'Bought a Persimmon home? Here\'s what to snag — and how to make it stick.',
        intro: 'Persimmon is one of the UK\'s largest housebuilders, completing around 10,000 new homes a year. The HBF Customer Satisfaction Survey has consistently scored Persimmon among the bottom tier of major builders for build quality and complaint handling — which is precisely why getting your snags documented properly matters.',
        commonDefects: [
            'Plasterboard nail pops and hairline cracks above doorways',
            'Loose or uneven decking and patio slabs in turfed gardens',
            'Skirting board gaps and unfinished caulking around architraves',
            'Boiler pressure issues and underfloor heating zones not balancing',
            'Render cracks at panel joins on render-finished plots',
            'Loft insulation not laid to building-regs depth (270mm minimum)',
            'Internal doors binding or out of square after first season',
        ],
        hbfRating: '3 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Persimmon homes are typically covered by the NHBC Buildmark 10-year structural warranty. The first 2 years are the builder defects period — log everything within that window.',
    },
    {
        slug: 'barratt-homes',
        name: 'Barratt Homes',
        legalName: 'Barratt Developments PLC',
        tagline: 'Snag your Barratt home properly — before the 2-year warranty window closes.',
        intro: 'Barratt is the UK\'s largest housebuilder by volume (now Barratt Redrow following the 2024 merger). Barratt homes are generally well-rated in the HBF survey, but the sheer volume of completions means individual plot quality varies dramatically by site and finisher.',
        commonDefects: [
            'Window seal gaps and condensation between glazing units',
            'Ill-fitting kitchen unit doors and drawer fronts',
            'Bathroom silicone cracks and grout shrinkage in shower trays',
            'Squeaky floorboards from incomplete acoustic underlay',
            'Driveway block-paving subsidence after first winter',
            'Garage door alignment issues',
            'Inadequate loft hatch insulation seals',
        ],
        hbfRating: '4 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Barratt homes typically carry the NHBC Buildmark warranty with a 2-year builder defects period followed by 8 years of NHBC structural cover.',
    },
    {
        slug: 'taylor-wimpey-homes',
        name: 'Taylor Wimpey',
        legalName: 'Taylor Wimpey PLC',
        tagline: 'Document every defect in your Taylor Wimpey home — share with one tap.',
        intro: 'Taylor Wimpey completes around 10,000–11,000 homes a year across the UK and consistently ranks in the upper-middle of the HBF Customer Satisfaction Survey. Their aftercare process is well-documented, but defects still need formal logging — verbal "I\'ll get it sorted" promises won\'t hold up if the work isn\'t done in time.',
        commonDefects: [
            'Render and brick cracking at expansion joints',
            'Internal door latch / strike-plate misalignment',
            'Boiler flue penetration sealing and roof flashing gaps',
            'Carpet seams visible at room transitions',
            'Garden levels not draining away from the property',
            'Driveway tarmac edge cracking',
            'Kitchen worktop joint sealant shrinkage',
        ],
        hbfRating: '4 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Taylor Wimpey uses NHBC Buildmark for the majority of its developments. Aftercare team operates on a service-ticket basis — every defect needs a written reference, which is exactly what DefectR generates.',
    },
    {
        slug: 'bellway-homes',
        name: 'Bellway Homes',
        legalName: 'Bellway PLC',
        tagline: 'Bought a Bellway home? Track every snag with photo evidence.',
        intro: 'Bellway is one of the UK\'s top-five housebuilders by volume, completing around 7,000 homes a year. Bellway scores well on the HBF survey for sales and handover but lower on post-occupation defects resolution speed.',
        commonDefects: [
            'Driveway settlement and edging stone movement',
            'External door threshold gaps letting in draught',
            'Loft hatch insulation strips not sealing properly',
            'Bath panel and shower screen seal gaps',
            'Power socket faceplates not flush with plaster',
            'Render colour inconsistency on multi-panel elevations',
            'Soil-vent-pipe boxing not flush with finished walls',
        ],
        hbfRating: '3 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Bellway homes are warranted by NHBC. The 2-year builder defects window is critical — anything you don\'t flag in writing during that period falls to your own cost after.',
    },
    {
        slug: 'vistry-homes',
        name: 'Vistry Homes',
        legalName: 'Vistry Group PLC',
        tagline: 'Vistry / Bovis / Linden / Countryside — one snag list, one shared report.',
        intro: 'Vistry Group now includes the former Bovis Homes, Linden Homes and Countryside Properties brands. If your home was sold under any of these brands since the 2022 merger, you are dealing with Vistry\'s aftercare team — and they handle defects on a per-development basis.',
        commonDefects: [
            'Carpet stretching gaps along skirting board joins',
            'Internal door undercuts not allowing airflow to extracts',
            'Kitchen splashback sealant shrinkage',
            'Garage floor surface cracking and dust generation',
            'Ensuite bathroom vinyl flooring lift around shower trays',
            'External tap insulation and isolation valve access',
            'Roof tile slippage on hip and verge details',
        ],
        hbfRating: '3 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Most Vistry / former-Bovis / former-Linden / former-Countryside homes carry the NHBC Buildmark 10-year warranty. Site manager handover packs may reference the legacy brand — but the warranty obligation now sits with Vistry Group.',
    },
    {
        slug: 'redrow-homes',
        name: 'Redrow Homes',
        legalName: 'Redrow PLC',
        tagline: 'Heritage-style Redrow home? Don\'t let traditional finishes hide modern snags.',
        intro: 'Redrow specialises in heritage and Arts-and-Crafts-inspired homes — well-regarded on architectural merit. Now part of the Barratt Redrow group following the 2024 merger. Heritage detailing means more surface area for snags to hide, especially in mouldings and external render.',
        commonDefects: [
            'External render cracks at decorative quoins and lintels',
            'Internal coving joins and ceiling rose alignment',
            'Bay window draught penetration at sash joins',
            'Inglenook fireplace flue or chimney breast cracking',
            'Driveway block-paving pattern misalignment',
            'Eaves soffit ventilation strip continuity',
            'Front porch tile pattern grout shrinkage',
        ],
        hbfRating: '5 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Redrow plots typically carry NHBC Buildmark 10-year warranty. Heritage detailing is more expensive to remedy late — log every single decorative element snag in the first 6 months.',
    },
    {
        slug: 'berkeley-homes',
        name: 'Berkeley Homes',
        legalName: 'The Berkeley Group Holdings PLC',
        tagline: 'Premium Berkeley home? Premium standards. Hold them to it.',
        intro: 'Berkeley Group focuses on London and South-East premium developments. Average sale prices well above £500k mean homeowners expect — and are entitled to — significantly higher build quality than mass-market builders. Most plots are apartments rather than houses.',
        commonDefects: [
            'Engineered timber flooring lifting at room transitions',
            'High-end kitchen door alignment under heavy use',
            'Underfloor heating zone responsiveness in larger apartments',
            'Communal area finishes (lift lobbies, corridors)',
            'Balcony decking fastener corrosion and water-pooling',
            'Apartment building-warranty boundaries vs leasehold service charge',
            'Mechanical ventilation (MVHR) commissioning balance',
        ],
        hbfRating: '5 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Berkeley plots often use Premier Guarantee or NHBC; check your handover pack. Apartment blocks have separate warranties for individual units vs communal areas — DefectR can track both on one property.',
    },
    {
        slug: 'crest-nicholson-homes',
        name: 'Crest Nicholson',
        legalName: 'Crest Nicholson Holdings PLC',
        tagline: 'Crest Nicholson home? Get every snag in writing — fast.',
        intro: 'Crest Nicholson focuses on placemaking and larger mixed-tenure developments across Southern England. The HBF survey rates Crest around the middle of the major builders, with stronger sales-team feedback than aftercare.',
        commonDefects: [
            'External brickwork pointing inconsistency',
            'Garden level and topsoil depth shortfall',
            'Sliding door alignment on bi-fold and patio openings',
            'Heating system zone control mapping errors',
            'Internal staircase squeaks from poor noggining',
            'Window furniture loose after first season',
            'Drive-through entrance lighting timer mis-set',
        ],
        hbfRating: '4 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Crest Nicholson plots usually carry NHBC Buildmark warranty. The 2-year builder defects window is your free shot — everything you log within it is the builder\'s legal obligation to fix.',
    },
    {
        slug: 'bloor-homes',
        name: 'Bloor Homes',
        legalName: 'Bloor Holdings Ltd',
        tagline: 'Bloor Homes snag list? Document it. Share it. Get it fixed.',
        intro: 'Bloor Homes is a private family-owned housebuilder operating across England, completing around 3,500 homes a year. Bloor scores consistently above industry average on the HBF survey, but defects still need formal documentation — assume nothing is fixed unless it\'s written down.',
        commonDefects: [
            'Render finish micro-cracks at expansion joints',
            'Driveway gravel migration into garden borders',
            'Bath panel and shower-tray sealant gaps',
            'Garage personal-door alignment',
            'Loft hatch draught and insulation seal',
            'Kitchen island electrical socket grounding',
            'External cladding panel fastener visibility',
        ],
        hbfRating: '5 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'Bloor Homes typically uses NHBC Buildmark or Premier Guarantee — check your handover pack. Both follow the standard 2-year builder defects + 8-year structural pattern.',
    },
    {
        slug: 'david-wilson-homes',
        name: 'David Wilson Homes',
        legalName: 'Barratt Developments PLC',
        tagline: 'David Wilson Homes (Barratt) snag list — protect your premium spec.',
        intro: 'David Wilson Homes is Barratt\'s premium brand, typically larger detached homes with higher-end finishes than the standard Barratt range. Now part of the Barratt Redrow group post-2024 merger. Premium spec means more high-end fittings — and more places for snags to hide.',
        commonDefects: [
            'Stone-effect worktop join sealant on premium kitchens',
            'Wardrobe and storage interior fit-out alignment',
            'Bay window structural lintel hairline cracks',
            'Underfloor heating manifold valve labelling and access',
            'External French door / orangery threshold sealing',
            'Driveway porous paving drainage',
            'High-spec lighting circuit zoning vs handover spec',
        ],
        hbfRating: '4 stars (2025 HBF Customer Satisfaction Survey)',
        nhbcWarrantyNote: 'David Wilson Homes plots carry NHBC Buildmark warranty under Barratt Developments PLC. Premium-spec items often need photographic evidence to claim — DefectR makes that one tap, not a folder of disorganised iPhone photos.',
    },
];

/** Lookup helper — returns null if no match. */
export function findHousebuilder(slug) {
    const norm = (slug || '').toLowerCase();
    return HOUSEBUILDERS.find(b => b.slug === norm) || null;
}
